import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2, X } from "lucide-react";
import { Link } from "react-router-dom";
import {
  createFoodMenuItem,
  fetchFoodCategories,
  fetchFoodIngredients,
  fetchFoodMenuItems,
  updateFoodMenuItem,
  type FoodMenuItem,
} from "../foodApi";
import { money, COMMON_UNITS } from "../utils/foodFormatters";
import { groupByCategory } from "../utils/groupByCategory";
import { QueryError } from "../components/QueryError";
import { GroupedTable } from "../components/GroupedTable";
import {
  ArchiveButton,
  ArchivedBadge,
  ListToolbar,
  useExpandedGroups,
} from "../components/ListToolbar";

const COLUMNS: { key: string; title: string; render: (r: FoodMenuItem) => ReactNode }[] = [
  {
    key: "name",
    title: "Name",
    render: (r) => (
      <>
        <Link to={`/food/menu/${r.id}`} className="text-tribal-red hover:underline">
          {r.name}
        </Link>
        {!r.isActive && <ArchivedBadge />}
      </>
    ),
  },
  { key: "price", title: "Price", render: (r) => money(r.currentPrice) },
  {
    key: "recipeCost",
    title: "Recipe Cost",
    render: (r) => {
      if (r.recipeCost == null) return "-";
      if (r.recipeCostComplete === false) {
        return (
          <span
            className="text-amber-400"
            title={`No usable cost for: ${
              r.unpricedIngredients?.map((i) => i.name).join(", ") ||
              `${r.unpricedIngredientCount ?? 0} ingredient(s)`
            }. This cost is too low.`}
          >
            {money(r.recipeCost)}*
          </span>
        );
      }
      return money(r.recipeCost);
    },
  },
  {
    key: "cost",
    title: "Est. Cost",
    render: (r) => (r.costOverride != null ? money(r.costOverride) : "-"),
  },
  { key: "ingredients", title: "Ingredients", render: (r) => r.ingredientCount },
];

function AddMenuItemModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const nameRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [price, setPrice] = useState("");
  const [costOverride, setCostOverride] = useState("");
  const [recipeRows, setRecipeRows] = useState<
    Array<{ ingredientId: number; quantity: string; unit: string }>
  >([]);
  const [error, setError] = useState<string | null>(null);

  const categoriesQuery = useQuery({
    queryKey: ["food", "categories"],
    queryFn: fetchFoodCategories,
  });
  const ingredientsQuery = useQuery({
    queryKey: ["food", "ingredients"],
    queryFn: fetchFoodIngredients,
  });

  const menuCategories = (categoriesQuery.data ?? [])
    .filter((c) => c.type === "menu" && c.isActive)
    .map((c) => c.name);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const createMutation = useMutation({
    mutationFn: () =>
      createFoodMenuItem({
        name: name.trim(),
        category,
        currentPrice: price.trim() ? Number(price) : null,
        costOverride: costOverride.trim() ? Number(costOverride) : null,
        ingredients: recipeRows
          .map((r) => ({
            ingredientId: r.ingredientId,
            quantity: Number(r.quantity),
            unit: r.unit,
          }))
          .filter((r) => Number.isFinite(r.ingredientId) && r.ingredientId > 0 && r.quantity > 0),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["food", "menu"] });
      queryClient.invalidateQueries({ queryKey: ["food", "dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["food", "analysis"] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Failed to create menu item");
    },
  });

  function handleSubmit() {
    setError(null);
    if (!name.trim()) { setError("Name is required."); return; }
    if (!category) { setError("Please select a category."); return; }
    if (price.trim() && isNaN(Number(price))) { setError("Price must be a number."); return; }
    if (costOverride.trim() && isNaN(Number(costOverride))) {
      setError("Estimated cost must be a number.");
      return;
    }
    createMutation.mutate();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-2xl rounded-xl border border-tribal-border bg-tribal-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-tribal-border px-5 py-4">
          <h2 className="text-lg font-semibold text-white">Add Menu Item</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-tribal-muted transition-colors hover:text-white"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto">
          <div className="space-y-4 p-5">
            <div>
              <label className="mb-1 block text-xs font-medium text-tribal-muted">Name</label>
              <input
                ref={nameRef}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Smash Burger"
                onKeyDown={(e) => { if (e.key === "Enter" && recipeRows.length === 0) handleSubmit(); }}
                className="w-full rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-tribal-muted">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-gray-300 focus:border-tribal-red focus:outline-none"
              >
                <option value="" disabled>Select a category…</option>
                {menuCategories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-tribal-muted">
                  Menu Price ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="14.99"
                  className="w-full rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-tribal-muted">
                  Est. Cost ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={costOverride}
                  onChange={(e) => setCostOverride(e.target.value)}
                  placeholder="4.50"
                  className="w-full rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
                />
              </div>
            </div>

            <p className="text-xs text-tribal-muted">
              Estimated cost overrides recipe-calculated cost. Leave blank to use ingredient costs from invoices.
            </p>

            <div className="border-t border-tribal-border pt-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-medium text-white">Recipe Builder</h3>
                <button
                  type="button"
                  onClick={() =>
                    setRecipeRows((prev) => [...prev, { ingredientId: 0, quantity: "", unit: "each" }])
                  }
                  className="inline-flex items-center gap-1 rounded-md bg-white/5 px-2.5 py-1 text-xs text-tribal-muted transition-colors hover:text-white"
                >
                  <Plus className="h-3 w-3" />
                  Add ingredient
                </button>
              </div>

              {recipeRows.length === 0 ? (
                <p className="rounded-lg border border-dashed border-tribal-border px-4 py-3 text-center text-xs text-tribal-muted">
                  No ingredients yet. Add ingredients to build the recipe, or add them later from the detail view.
                </p>
              ) : (
                <div className="space-y-2">
                  {recipeRows.map((row, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-[1fr_80px_100px_32px] items-center gap-2"
                    >
                      <select
                        value={row.ingredientId}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          setRecipeRows((prev) =>
                            prev.map((r, i) => (i === idx ? { ...r, ingredientId: value } : r)),
                          );
                        }}
                        className="rounded-lg border border-tribal-border bg-tribal-bg px-2 py-1.5 text-sm text-gray-300 focus:border-tribal-red focus:outline-none"
                      >
                        <option value={0}>Select ingredient</option>
                        {(ingredientsQuery.data ?? []).filter((opt) => opt.isActive).map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.name}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={row.quantity}
                        onChange={(e) =>
                          setRecipeRows((prev) =>
                            prev.map((r, i) => (i === idx ? { ...r, quantity: e.target.value } : r)),
                          )
                        }
                        placeholder="Qty"
                        className="rounded-lg border border-tribal-border bg-tribal-bg px-2 py-1.5 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
                      />
                      <select
                        value={row.unit}
                        onChange={(e) =>
                          setRecipeRows((prev) =>
                            prev.map((r, i) => (i === idx ? { ...r, unit: e.target.value } : r)),
                          )
                        }
                        className="rounded-lg border border-tribal-border bg-tribal-bg px-2 py-1.5 text-sm text-gray-300 focus:border-tribal-red focus:outline-none"
                      >
                        {COMMON_UNITS.map((unit) => (
                          <option key={unit} value={unit}>{unit}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setRecipeRows((prev) => prev.filter((_, i) => i !== idx))}
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-tribal-muted transition-colors hover:bg-red-500/10 hover:text-red-400"
                        aria-label="Remove ingredient"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {error && (
              <p className="rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">
                {error}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-tribal-border px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-tribal-muted transition-colors hover:text-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            className="rounded-lg bg-tribal-red px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-tribal-red-dark disabled:opacity-50"
          >
            {createMutation.isPending ? "Creating…" : "Add Menu Item"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function MenuView() {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const q = useQuery({ queryKey: ["food", "menu"], queryFn: fetchFoodMenuItems });
  const searching = search.trim() !== "";
  const groupsState = useExpandedGroups(searching);

  const archiveMutation = useMutation({
    mutationFn: (r: FoodMenuItem) => updateFoodMenuItem(r.id, { isActive: !r.isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["food"] });
    },
  });

  const all = q.data ?? [];
  const activeCount = all.filter((r) => r.isActive).length;
  const archivedCount = all.length - activeCount;
  const groups = useMemo(
    () => groupByCategory(showArchived ? all : all.filter((r) => r.isActive), search),
    [all, showArchived, search],
  );

  const columns = [
    ...COLUMNS,
    {
      key: "actions",
      title: "",
      render: (r: FoodMenuItem) => (
        <ArchiveButton
          isActive={r.isActive}
          pending={archiveMutation.isPending && archiveMutation.variables?.id === r.id}
          onClick={() => archiveMutation.mutate(r)}
        />
      ),
    },
  ];

  if (q.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-sm text-tribal-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading menu...
      </div>
    );
  }
  if (q.isError || !q.data) {
    return <QueryError message={`Failed to load menu: ${(q.error as Error).message}`} />;
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-medium text-tribal-muted">
          {activeCount} active menu item{activeCount !== 1 ? "s" : ""}
          {archivedCount > 0 && ` · ${archivedCount} archived`}
        </h2>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-tribal-red px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-tribal-red-dark"
        >
          <Plus className="h-4 w-4" />
          Add Menu Item
        </button>
      </div>
      <ListToolbar
        search={search}
        onSearch={setSearch}
        placeholder="Search menu"
        showArchived={showArchived}
        onShowArchived={setShowArchived}
        onExpandAll={() => groupsState.expandAll(groups.map((g) => g.category))}
        onCollapseAll={groupsState.collapseAll}
      />
      {archiveMutation.isError && (
        <p className="mb-3 rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">
          {(archiveMutation.error as Error).message}
        </p>
      )}
      <section className="rounded-lg border border-tribal-border bg-tribal-surface">
        <GroupedTable<FoodMenuItem>
          groups={groups}
          columns={columns}
          isExpanded={groupsState.isExpanded}
          onToggle={groupsState.toggle}
          rowClassName={(r) => (r.isActive ? "" : "opacity-60")}
          empty={searching ? `No menu items match "${search.trim()}".` : "No active menu items found."}
        />
      </section>
      {showAdd && <AddMenuItemModal onClose={() => setShowAdd(false)} />}
    </div>
  );
}
